/**
 * Entity references with hover cards (DesignSystem 5.5): a machine, session, project or
 * member name anywhere in the product shows its status and quick actions on hover or focus.
 * The card reads live state while open, so it is always current.
 */
import { CircleStop, Copy, FolderGit2, ScrollText, Server, SquareTerminal, UserRound } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { machineLiveness } from '@orgus/protocol';
import type { Session } from '@orgus/protocol';
import {
  Avatar,
  Button,
  EntityHoverCard,
  RelativeTime,
  StatusDot,
  cn,
  formatBytes,
  formatCompact,
  formatPercent,
  machineLabel,
  machineTone,
  plural,
  sessionLabel,
  sessionTone,
  shortPath,
  toast,
  useNow,
} from '@orgus/ui';
import { useShallow } from 'zustand/react/shallow';
import { useOrgus } from '../../lib/store.ts';
import { summaryLine } from '../../lib/plainText.ts';
import { machineSystem } from '../machines/MachineCard.tsx';
import { stopSessionsAction } from '../act/catalogue.ts';
import { performAct } from '../act/run.ts';
import { modelLabel, tokenTotal } from '../sessions/labels.ts';
import { whereText } from '../ask/parse.ts';
import { shownTitle } from '../sessions/shownTitle.ts';

const REF = 'min-w-0 truncate rounded-control underline-offset-2 decoration-border-strong hover:underline focus-visible:underline';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="shrink-0 text-fg-subtle">{label}</dt>
      <dd className="min-w-0 truncate text-right text-fg-muted num">{children}</dd>
    </div>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border-subtle pt-3">{children}</div>;
}

function copy(text: string, what: string) {
  void navigator.clipboard
    ?.writeText(text)
    .then(() => toast.success(`Copied ${what}`))
    .catch(() => toast(`Copy ${what}`, { description: text }));
}

// ---------------------------------------------------------------------------
// Machine
// ---------------------------------------------------------------------------

export function MachineCardBody({ id }: { id: string }) {
  const m = useOrgus((s) => s.machines[id]);
  const live = useOrgus((s) => Object.values(s.sessions).filter((x) => x.machineId === id && x.status !== 'ended').length);
  const now = useNow(5_000);
  const navigate = useNavigate();
  if (!m) return <p className="text-sm text-fg-muted">This machine was removed from Orgus.</p>;
  const l = machineLiveness(m, now);
  return (
    <div>
      <div className="flex items-center gap-2">
        <Server className="size-4 text-fg-subtle" aria-hidden />
        <p className="min-w-0 flex-1 truncate font-medium text-fg">{m.name}</p>
        <StatusDot tone={machineTone(m, now)} size="sm" label="" />
        <span className="text-sm text-fg-muted">{machineLabel(m, now)}</span>
      </div>
      <dl className="mt-2.5 flex flex-col gap-1">
        <Row label="System">{machineSystem(m)}</Row>
        <Row label="Load">{l === 'offline' ? 'Not reporting' : formatPercent(m.load)}</Row>
        <Row label="Memory">
          {formatBytes(m.memTotal - m.memFree)} of {formatBytes(m.memTotal)}
        </Row>
        <Row label="Live sessions">{live}</Row>
        <Row label="Agent">{m.agentVersion}</Row>
        <Row label="Last seen">
          <RelativeTime ts={m.lastSeen} />
        </Row>
      </dl>
      <Actions>
        <Button size="sm" variant="secondary" onClick={() => navigate(`/machines?machine=${encodeURIComponent(id)}`)}>
          Open
        </Button>
        <Button size="sm" variant="ghost" icon={<SquareTerminal />} onClick={() => navigate(`/sessions?machine=${encodeURIComponent(id)}`)}>
          Sessions
        </Button>
        <Button size="sm" variant="ghost" icon={<Copy />} onClick={() => copy(m.hostname, 'host name')}>
          Host name
        </Button>
      </Actions>
    </div>
  );
}

export function MachineRef({ id, className, children }: { id: string; className?: string; children?: ReactNode }) {
  const name = useOrgus((s) => s.machines[id]?.name);
  return (
    <EntityHoverCard content={() => <MachineCardBody id={id} />}>
      <Link to={`/machines?machine=${encodeURIComponent(id)}`} onClick={(e) => e.stopPropagation()} className={cn(REF, className)}>
        {children ?? name ?? 'Removed machine'}
      </Link>
    </EntityHoverCard>
  );
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

function SessionCardBody({ id }: { id: string }) {
  const s = useOrgus((st) => st.sessions[id]);
  const machine = useOrgus((st) => (s ? st.machines[s.machineId] : undefined));
  const project = useOrgus((st) => (s?.projectId ? st.projects[s.projectId] : undefined));
  const navigate = useNavigate();
  if (!s) return <p className="text-sm text-fg-muted">This session is no longer reported.</p>;
  const stop = (x: Session) => {
    const st = useOrgus.getState();
    void performAct(stopSessionsAction([x], st, `session.stop:${x.id}`));
  };
  return (
    <div>
      <div className="flex items-start gap-2">
        <StatusDot tone={sessionTone(s)} size="sm" label="" className="mt-1.5" />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 font-medium text-fg">{shownTitle(s).title}</p>
          <p className="text-sm text-fg-muted">{sessionLabel(s)}</p>
        </div>
      </div>
      {s.lastText && <p className="mt-2 line-clamp-3 text-sm text-fg-muted">{summaryLine(s.lastText)}</p>}
      <dl className="mt-2.5 flex flex-col gap-1">
        <Row label="Project">{project?.name ?? 'No project'}</Row>
        <Row label="Machine">{machine?.name ?? 'Unknown'}</Row>
        <Row label="Folder">{shortPath(s.cwd, 36)}</Row>
        <Row label="Model">{modelLabel(s.model)}</Row>
        <Row label="Tokens">{formatCompact(tokenTotal(s))}</Row>
        <Row label="Active">
          <RelativeTime ts={s.lastActivity} />
        </Row>
      </dl>
      <Actions>
        <Button size="sm" variant="secondary" onClick={() => navigate(`/sessions?session=${encodeURIComponent(id)}`)}>
          Open
        </Button>
        {s.status !== 'ended' && (
          <Button size="sm" variant="ghost" icon={<CircleStop />} onClick={() => stop(s)}>
            Stop
          </Button>
        )}
        <Button size="sm" variant="ghost" icon={<Copy />} onClick={() => copy(s.id, 'session id')}>
          Id
        </Button>
      </Actions>
    </div>
  );
}

export function SessionRef({ id, className, children }: { id: string; className?: string; children?: ReactNode }) {
  const raw = useOrgus((s) => s.sessions[id]?.title);
  const title = raw ? shownTitle({ title: raw }).title : undefined;
  return (
    <EntityHoverCard content={() => <SessionCardBody id={id} />}>
      <Link to={`/sessions?session=${encodeURIComponent(id)}`} onClick={(e) => e.stopPropagation()} className={cn(REF, className)}>
        {children ?? title ?? 'Session'}
      </Link>
    </EntityHoverCard>
  );
}

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

function ProjectCardBody({ id }: { id: string }) {
  const p = useOrgus((s) => s.projects[id]);
  const live = useOrgus((s) => Object.values(s.sessions).filter((x) => x.projectId === id && x.status !== 'ended').length);
  const checkouts = useOrgus(useShallow((s) => Object.values(s.checkouts).filter((c) => c.projectId === id)));
  const machineNames = useOrgus((s) => [...new Set(checkouts.map((c) => s.machines[c.machineId]?.name).filter(Boolean))].join(', '));
  const navigate = useNavigate();
  if (!p) return <p className="text-sm text-fg-muted">This project is no longer watched.</p>;
  const dirty = checkouts.filter((c) => c.dirty > 0).length;
  return (
    <div>
      <div className="flex items-center gap-2">
        <Avatar name={p.name} colorKey={p.id} size={20} square />
        <p className="min-w-0 flex-1 truncate font-medium text-fg">{p.name}</p>
      </div>
      {p.remoteUrl && <p className="mt-1 truncate font-mono text-caption text-fg-subtle">{p.remoteUrl}</p>}
      <dl className="mt-2.5 flex flex-col gap-1">
        <Row label="Live sessions">{live}</Row>
        <Row label="Checkouts">
          {plural(checkouts.length, 'checkout')}
          {dirty ? `, ${dirty} with changes` : ''}
        </Row>
        <Row label="On">{machineNames || 'No machine'}</Row>
        <Row label="Main branch">{p.defaultBranch}</Row>
      </dl>
      <Actions>
        <Button size="sm" variant="secondary" icon={<FolderGit2 />} onClick={() => navigate(`/projects?project=${encodeURIComponent(id)}`)}>
          Open
        </Button>
        <Button size="sm" variant="ghost" icon={<SquareTerminal />} onClick={() => navigate(`/sessions?project=${encodeURIComponent(id)}`)}>
          Sessions
        </Button>
      </Actions>
    </div>
  );
}

export function ProjectRef({ id, className, children }: { id: string; className?: string; children?: ReactNode }) {
  const name = useOrgus((s) => s.projects[id]?.name);
  return (
    <EntityHoverCard content={() => <ProjectCardBody id={id} />}>
      <Link to={`/projects?project=${encodeURIComponent(id)}`} onClick={(e) => e.stopPropagation()} className={cn(REF, className)}>
        {children ?? name ?? 'Unknown project'}
      </Link>
    </EntityHoverCard>
  );
}

// ---------------------------------------------------------------------------
// Member (who did something)
// ---------------------------------------------------------------------------

function MemberCardBody({ login }: { login: string }) {
  const actions = useOrgus(useShallow((s) => s.audit.filter((e) => e.actor === login)));
  const failed = actions.filter((e) => e.result === 'error').length;
  const navigate = useNavigate();
  const agent = login.startsWith('agent:');
  return (
    <div>
      <div className="flex items-center gap-2">
        {agent ? <UserRound className="size-5 text-fg-subtle" aria-hidden /> : <Avatar name={login} colorKey={login} size={20} />}
        <p className="min-w-0 flex-1 truncate font-medium text-fg">{login}</p>
        <span className="text-sm text-fg-muted">{login === 'owner' ? 'Owner' : agent ? 'AI agent' : 'Member'}</span>
      </div>
      <dl className="mt-2.5 flex flex-col gap-1">
        <Row label="Actions loaded">{actions.length}</Row>
        <Row label="Failed">{failed}</Row>
        <Row label="Last action">{actions[0] ? <RelativeTime ts={actions[0].at} /> : 'None'}</Row>
      </dl>
      <Actions>
        <Button size="sm" variant="secondary" icon={<ScrollText />} onClick={() => navigate(`/audit?where=${encodeURIComponent(whereText([{ kind: 'actor', value: login, exact: true }]))}`)}>
          Their actions
        </Button>
      </Actions>
    </div>
  );
}

export function MemberRef({ login, className }: { login: string; className?: string }) {
  return (
    <EntityHoverCard content={() => <MemberCardBody login={login} />}>
      <button type="button" onClick={(e) => e.stopPropagation()} className={cn(REF, 'text-left', className)}>
        {login}
      </button>
    </EntityHoverCard>
  );
}
